import type {
  ChartBlock,
  ChartKind,
  ChartLegendPlacement,
  ChartRow,
  ChartState,
  Severity,
} from "../types";
import { normalizeText } from "../../ui/utils";
import { resolveColor } from "./chartTheme";

const clean = (s: string | undefined): string | undefined => (s ? normalizeText(s) : s);

export interface NormalizedSeries {
  key: string;
  label: string;
  color: string;
}

export type LegendPlacement = "top" | "bottom" | "right" | "none";

export interface NormalizedChart {
  kind: ChartKind;
  title?: string;
  subtitle?: string;
  unit: string;
  stacked: boolean;
  legend: LegendPlacement;
  xLabel?: string;
  yLabel?: string;
  categoryKey: string;
  series: NormalizedSeries[];
  rows: Array<ChartRow & { fill?: string }>;
  singleSeries: boolean;
  total: number;
}

const LEGACY_KIND: Record<NonNullable<ChartBlock["chart"]>, ChartKind> = {
  donut: "doughnut",
  bar: "bar",
  line: "line",
};

export function isRadial(kind: ChartKind): boolean {
  return kind === "pie" || kind === "doughnut";
}

export function isHorizontal(kind: ChartKind): boolean {
  return kind === "horizontalBar";
}

export function kindLabel(kind: ChartKind): string {
  switch (kind) {
    case "horizontalBar":
      return "horizontal bar";
    case "doughnut":
      return "doughnut";
    default:
      return kind;
  }
}

const KNOWN_CHART_STATES: ChartState[] = ["loading", "success", "empty", "nodata", "error"];

export function deriveState(spec: ChartBlock | null | undefined, loading?: boolean): ChartState {
  if (loading) return "loading";
  if (!spec) return "nodata";
  if (spec.state) {
    if (KNOWN_CHART_STATES.includes(spec.state)) return spec.state;
    // Unrecognized state string from an untrusted spec: don't render a blank
    // chart with no state UI — fall back based on whether data is present.
    return Array.isArray(spec.data) && spec.data.length > 0 ? "success" : "nodata";
  }
  if (!Array.isArray(spec.data)) return "nodata";
  if (spec.data.length === 0) return "empty";
  return "success";
}

export function isAllZero(n: NormalizedChart): boolean {
  return n.rows.every((r) => n.series.every((s) => Number(r[s.key]) === 0));
}

function looksTemporal(labels: string[]): boolean {
  if (labels.length < 3) return false;
  const temporal =
    /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|q[1-4]\b|week|wk|day|mon|tue|wed|thu|fri|sat|sun|\d{4}|\d{1,2}[/-]\d{1,2})/i;
  const hits = labels.filter((l) => temporal.test(l.trim())).length;
  return hits >= Math.ceil(labels.length * 0.6);
}

function longestLabel(labels: string[]): number {
  return labels.reduce((max, l) => Math.max(max, l.length), 0);
}

export function autoKind(categoryKey: string, seriesCount: number, rows: ChartRow[]): ChartKind {
  const labels = rows.map((r) => String(r[categoryKey] ?? ""));
  const temporal = looksTemporal(labels);

  if (seriesCount > 1) return temporal ? "line" : "bar";
  if (temporal) return labels.length > 8 ? "area" : "line";
  if (labels.length <= 5 && longestLabel(labels) <= 10) return "doughnut";
  if (labels.length <= 6 && longestLabel(labels) <= 6) return "bar";
  return "horizontalBar";
}

function resolveLegend(
  pref: ChartLegendPlacement,
  kind: ChartKind,
  seriesCount: number,
): LegendPlacement {
  if (pref !== "auto") return pref;
  if (isRadial(kind)) return "right";
  return seriesCount > 1 ? "top" : "none";
}

function asSeverity(v: unknown): Severity | undefined {
  return typeof v === "string" &&
    ["critical", "high", "medium", "low", "info"].includes(v)
    ? (v as Severity)
    : undefined;
}

export function normalizeChart(spec: ChartBlock): NormalizedChart {
  const categoryKey = spec.xKey ?? "label";
  const unit = spec.unit ?? "";
  const rawRows = Array.isArray(spec.data) ? spec.data : [];

  const hasSeries = Array.isArray(spec.series) && spec.series.length > 0;
  const seriesDefs = hasSeries ? spec.series! : [{ key: "value", label: "Value" }];
  const series: NormalizedSeries[] = seriesDefs.map((s, i) => ({
    key: s.key,
    label: clean(s.label ?? s.key) ?? s.key,
    color: resolveColor({ color: s.color }, i),
  }));
  const singleSeries = series.length === 1;

  const rows = rawRows.map((raw, i) => {
    // Defensive: a malformed spec could carry a null/primitive row; coerce so
    // the property access below never throws during render.
    const r = raw && typeof raw === "object" ? raw : ({} as ChartRow);
    const row: ChartRow & { fill?: string } = { ...r };
    row[categoryKey] = normalizeText(String(r[categoryKey] ?? ""));
    for (const s of series) {
      const v = Number(r[s.key]);
      row[s.key] = Number.isFinite(v) ? v : 0;
    }
    if (singleSeries) {
      row.fill = resolveColor(
        { color: typeof r.color === "string" ? r.color : undefined, severity: asSeverity(r.severity) },
        i,
      );
    }
    return row;
  });

  const kindPref = spec.kind ?? (spec.chart ? LEGACY_KIND[spec.chart] : "auto");
  const kind = kindPref === "auto" ? autoKind(categoryKey, series.length, rows) : kindPref;

  const total = rows.reduce((sum, r) => {
    const v = Number(r[series[0].key]);
    return sum + (Number.isFinite(v) ? v : 0);
  }, 0);

  return {
    kind,
    title: clean(spec.title),
    subtitle: clean(spec.subtitle),
    unit,
    stacked: !!spec.stacked,
    legend: resolveLegend(spec.legend ?? "auto", kind, series.length),
    xLabel: clean(spec.xLabel),
    yLabel: clean(spec.yLabel),
    categoryKey,
    series,
    rows,
    singleSeries,
    total,
  };
}

export function buildSrSummary(n: NormalizedChart): string {
  const head = `${n.title ? `${n.title}. ` : ""}${kindLabel(n.kind)} chart.`;
  const rows = n.rows.slice(0, 24).map((r) => {
    const label = String(r[n.categoryKey] ?? "");
    if (n.singleSeries) return `${label}: ${r[n.series[0].key]}${n.unit}`;
    const parts = n.series.map((s) => `${s.label} ${r[s.key]}${n.unit}`).join(", ");
    return `${label}: ${parts}`;
  });
  const more = n.rows.length > 24 ? `; and ${n.rows.length - 24} more` : "";
  return `${head} ${rows.join("; ")}${more}.`;
}
